import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  SquareCheck,
  Square,
  ShieldAlert,
  Download,
} from 'lucide-react'
import { Button, FilterBar, FilterChip, EmptyState, LoadingState, Modal, Input } from '@/components/ui'
import type { ArticleSyncConflict } from '../shared.ts'
import { formatCurrency } from '../shared.ts'

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportConflictsCsv(conflicts: ArticleSyncConflict[]) {
  const header = ['Artikelnr.', 'Artikelname', 'Feld', 'Mandant', 'Zentraler Wert', 'SAP-Wert', 'Seit']
  const rows = conflicts.map((c) => [
    c.articleItemCode ?? '',
    c.articleItemName ?? '',
    FIELD_LABELS[c.field] ?? c.field,
    c.tenantCode ?? '',
    c.centralValue ?? '',
    c.remoteValue ?? '',
    c.createdAt ? new Date(c.createdAt).toLocaleDateString('de-DE') : '',
  ])
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `artikelkonflikte_${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Age helper ───────────────────────────────────────────────────────────────

function formatAge(createdAt: string | undefined): string {
  if (!createdAt) return ''
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000)
  if (days === 0) return 'heute'
  if (days === 1) return '1 Tag'
  return `${days} Tage`
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConflictStats {
  total: number
  byField: Record<string, number>
}

interface AutoResolveResult {
  resolved: number
  skipped: number
  details: Array<{ id: string; decision: 'local' | 'remote'; reason: string }>
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  itemName: 'Artikelname',
  salesPrice: 'Verkaufspreis',
  purchasePrice: 'Einkaufspreis',
  unit: 'Mengeneinheit',
  Mengeneinheit: 'Mengeneinheit',
}

const PAGE_SIZE = 50

// ─── Conflicts Section ────────────────────────────────────────────────────────

export function ConflictsSection() {
  const queryClient = useQueryClient()

  const [fieldFilter, setFieldFilter] = useState<string>('all')
  const [tenantFilter, setTenantFilter] = useState<string>('all')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [autoResolveResult, setAutoResolveResult] = useState<AutoResolveResult | null>(null)
  const [showAutoDetails, setShowAutoDetails] = useState(false)

  const { data: conflicts = [], isLoading } = useQuery<ArticleSyncConflict[]>({
    queryKey: ['article-conflicts'],
    queryFn: () => apiClient.get('/articles/conflicts').then((r) => r.data),
    retry: false,
  })

  const { data: stats } = useQuery<ConflictStats>({
    queryKey: ['article-conflict-stats'],
    queryFn: () => apiClient.get('/articles/conflicts/stats').then((r) => r.data),
    retry: false,
  })

  const resolveMutation = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: 'local' | 'remote' }) =>
      apiClient.post(`/articles/conflicts/${id}/resolve`, { resolution }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/articles/conflicts/${id}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
    },
  })

  const bulkResolveMutation = useMutation({
    mutationFn: ({ ids, resolution }: { ids: string[]; resolution: 'local' | 'remote' }) =>
      apiClient.post('/articles/conflicts/bulk-resolve', { ids, resolution }),
    onSuccess: () => {
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const bulkDismissMutation = useMutation({
    mutationFn: (ids: string[]) =>
      apiClient.post('/articles/conflicts/bulk-dismiss', { ids }),
    onSuccess: () => {
      setSelected(new Set())
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
    },
  })

  const dismissUnitsMutation = useMutation({
    mutationFn: () => apiClient.post('/articles/conflicts/auto-dismiss-units', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
    },
  })

  const acceptSapUnitsMutation = useMutation({
    mutationFn: () => apiClient.post('/articles/conflicts/accept-sap-units', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  const autoResolveMutation = useMutation({
    mutationFn: () => apiClient.post('/articles/conflicts/auto-resolve-names', {}),
    onSuccess: (response) => {
      setAutoResolveResult(response.data as AutoResolveResult)
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      queryClient.invalidateQueries({ queryKey: ['articles'] })
    },
  })

  // Distinct tenants for filter
  const tenants = useMemo(() => {
    const seen = new Set<string>()
    return conflicts
      .filter((c) => c.tenantCode)
      .map((c) => ({ id: c.tenantId, code: c.tenantCode! }))
      .filter((t) => { if (seen.has(t.id)) return false; seen.add(t.id); return true })
  }, [conflicts])

  // Filtered + paginated conflicts
  const filtered = useMemo(() => {
    return conflicts.filter((c) => {
      if (fieldFilter !== 'all' && c.field !== fieldFilter) return false
      if (tenantFilter !== 'all' && c.tenantId !== tenantFilter) return false
      return true
    })
  }, [conflicts, fieldFilter, tenantFilter])

  const paginated = useMemo(() => {
    return filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
  }, [filtered, page])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)

  // Selection helpers
  const isAllOnPageSelected =
    paginated.length > 0 && paginated.every((c) => selected.has(c.id))

  function toggleAll() {
    if (isAllOnPageSelected) {
      const next = new Set(selected)
      paginated.forEach((c) => next.delete(c.id))
      setSelected(next)
    } else {
      const next = new Set(selected)
      paginated.forEach((c) => next.add(c.id))
      setSelected(next)
    }
  }

  function toggleOne(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const selectedIds = Array.from(selected)
  const isBulkPending = bulkResolveMutation.isPending || bulkDismissMutation.isPending
  const nameConflictCount = stats?.byField['itemName'] ?? 0
  const unitConflictCount = (stats?.byField['unit'] ?? 0) + (stats?.byField['Mengeneinheit'] ?? 0)

  if (isLoading) {
    return <LoadingState label="Konflikte werden geladen…" />
  }

  if (conflicts.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="w-10 h-10 text-green-400" />}
        title="Keine offenen Konflikte"
        description="Alle Artikel sind synchron."
      />
    )
  }

  return (
    <div className="space-y-4">
      {/* Stats header */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-700">
          {conflicts.length} offene Konflikte
        </span>
        {stats && (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(stats.byField).map(([field, count]) => (
              <span
                key={field}
                className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
              >
                {FIELD_LABELS[field] ?? field}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Auto-resolve result banner */}
      {autoResolveResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">
                Auto-Auflösung abgeschlossen: {autoResolveResult.resolved} aufgelöst,{' '}
                {autoResolveResult.skipped} übersprungen (Gleichstand oder leer)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAutoDetails(!showAutoDetails)}
                className="text-xs text-green-600 hover:underline flex items-center gap-1"
              >
                Details
                {showAutoDetails ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>
              <button
                onClick={() => setAutoResolveResult(null)}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
          {showAutoDetails && autoResolveResult.details.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
              {autoResolveResult.details.slice(0, 20).map((d) => (
                <p key={d.id} className="text-xs text-green-700">
                  → {d.reason} ({d.decision === 'local' ? 'Lokal behalten' : 'SAP übernommen'})
                </p>
              ))}
              {autoResolveResult.details.length > 20 && (
                <p className="text-xs text-green-600 italic">
                  … und {autoResolveResult.details.length - 20} weitere
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Field filter */}
        <FilterBar>
          <FilterChip
            active={fieldFilter === 'all'}
            count={conflicts.length}
            onClick={() => { setFieldFilter('all'); setPage(0) }}
          >
            Alle
          </FilterChip>
          {stats &&
            Object.entries(stats.byField).map(([field, count]) => (
              <FilterChip
                key={field}
                active={fieldFilter === field}
                count={count}
                onRemove={fieldFilter === field ? () => { setFieldFilter('all'); setPage(0) } : undefined}
                onClick={() => { setFieldFilter(field); setPage(0) }}
              >
                {FIELD_LABELS[field] ?? field}
              </FilterChip>
            ))}
        </FilterBar>

        <div className="ml-auto flex items-center gap-2">
          {/* Auto-dismiss unit conflicts */}
          {unitConflictCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dismissUnitsMutation.mutate()}
              disabled={dismissUnitsMutation.isPending}
            >
              {dismissUnitsMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              Einheiten normalisieren ({unitConflictCount})
            </Button>
          )}

          {/* Accept SAP unit values */}
          {unitConflictCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => acceptSapUnitsMutation.mutate()}
              disabled={acceptSapUnitsMutation.isPending}
              title="SAP-Einheiten für alle offenen Einheiten-Konflikte übernehmen (z.B. 'Stück pro Monat' statt 'Stk')"
            >
              {acceptSapUnitsMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : null}
              SAP-Einheiten übernehmen ({unitConflictCount})
            </Button>
          )}

          {/* Auto-resolve button (only for name conflicts) */}
          {nameConflictCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => autoResolveMutation.mutate()}
              disabled={autoResolveMutation.isPending}
            >
              {autoResolveMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              Auto-Auflösung Artikelname ({nameConflictCount})
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => exportConflictsCsv(filtered)}
            title="Gefilterte Konflikte als CSV exportieren"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </Button>
        </div>
      </div>

      {/* Mandanten-Filter */}
      {tenants.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Mandant:</span>
          <FilterBar>
            <FilterChip
              active={tenantFilter === 'all'}
              onClick={() => { setTenantFilter('all'); setPage(0) }}
            >
              Alle
            </FilterChip>
            {tenants.map((t) => (
              <FilterChip
                key={t.id}
                active={tenantFilter === t.id}
                onRemove={tenantFilter === t.id ? () => { setTenantFilter('all'); setPage(0) } : undefined}
                onClick={() => { setTenantFilter(t.id); setPage(0) }}
              >
                {t.code}
              </FilterChip>
            ))}
          </FilterBar>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-blue-700">
            {selected.size} ausgewählt
          </span>
          <div className="flex gap-2 ml-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => bulkResolveMutation.mutate({ ids: selectedIds, resolution: 'local' })}
              disabled={isBulkPending}
            >
              Lokal behalten
            </Button>
            <Button
              variant="accent"
              size="xs"
              onClick={() => bulkResolveMutation.mutate({ ids: selectedIds, resolution: 'remote' })}
              disabled={isBulkPending}
            >
              SAP-Wert übernehmen
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => bulkDismissMutation.mutate(selectedIds)}
              disabled={isBulkPending}
            >
              Verwerfen
            </Button>
          </div>
          {isBulkPending && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
          <button
            className="ml-auto text-xs text-blue-500 hover:underline"
            onClick={() => setSelected(new Set())}
          >
            Auswahl aufheben
          </button>
        </div>
      )}

      {/* Select all on page */}
      <div className="flex items-center gap-2">
        <button
          onClick={toggleAll}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          {isAllOnPageSelected ? (
            <SquareCheck className="w-4 h-4 text-blue-500" />
          ) : (
            <Square className="w-4 h-4" />
          )}
          Alle auf dieser Seite auswählen
        </button>
        <span className="text-xs text-gray-400">
          Seite {page + 1} von {totalPages} ({filtered.length} Konflikte)
        </span>
      </div>

      {/* Conflict cards */}
      <div className="space-y-3">
        {paginated.map((c) => (
          <ConflictCard
            key={c.id}
            conflict={c}
            selected={selected.has(c.id)}
            onToggle={() => toggleOne(c.id)}
            onResolve={(resolution) => resolveMutation.mutate({ id: c.id, resolution })}
            onDismiss={() => dismissMutation.mutate(c.id)}
            isPending={resolveMutation.isPending || dismissMutation.isPending}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Zurück
          </Button>
          <span className="text-sm text-gray-600">
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Weiter
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Price Exception Dialog ───────────────────────────────────────────────────

interface PriceExceptionDialogProps {
  articleId: string
  tenantId: string
  tenantCode: string
  onClose: () => void
}

function PriceExceptionDialog({ articleId, tenantId, tenantCode, onClose }: PriceExceptionDialogProps) {
  const queryClient = useQueryClient()
  const [salesPrice, setSalesPrice] = useState('')
  const [purchasePrice, setPurchasePrice] = useState('')
  const [note, setNote] = useState('')

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put(`/articles/${articleId}/price-exceptions/${tenantId}`, {
        salesPrice: salesPrice ? parseFloat(salesPrice) : undefined,
        purchasePrice: purchasePrice ? parseFloat(purchasePrice) : undefined,
        note: note || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['article-conflicts'] })
      queryClient.invalidateQueries({ queryKey: ['article-conflict-stats'] })
      onClose()
    },
  })

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={`Preisausnahme für Mandant ${tenantCode}`}
      description="Definiere mandantenspezifische Preise. Preiskonflikte für diese Kombination werden künftig automatisch übersprungen."
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Speichern & Konflikte verwerfen
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input
          label="Verkaufspreis (€)"
          type="number"
          step="0.01"
          placeholder="z.B. 95.00"
          value={salesPrice}
          onChange={(e) => setSalesPrice(e.target.value)}
          hint="Leer lassen = zentraler Preis gilt"
        />
        <Input
          label="Einkaufspreis (€)"
          type="number"
          step="0.01"
          placeholder="z.B. 70.00"
          value={purchasePrice}
          onChange={(e) => setPurchasePrice(e.target.value)}
          hint="Leer lassen = zentraler Preis gilt"
        />
        <Input
          label="Notiz (optional)"
          placeholder="z.B. Stundensatz für Mandant imw"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  )
}

// ─── Conflict Card ─────────────────────────────────────────────────────────

interface ConflictCardProps {
  conflict: ArticleSyncConflict
  selected: boolean
  onToggle: () => void
  onResolve: (resolution: 'local' | 'remote') => void
  onDismiss: () => void
  isPending: boolean
}

function ConflictCard({
  conflict: c,
  selected,
  onToggle,
  onResolve,
  onDismiss,
  isPending,
}: ConflictCardProps) {
  const [showPriceException, setShowPriceException] = useState(false)
  const isPrice = c.field.includes('Price')

  return (
    <div
      className={`border rounded-lg p-4 transition-colors ${
        selected ? 'border-blue-300 bg-blue-50/30' : 'border-amber-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button onClick={onToggle} className="mt-0.5 flex-shrink-0">
          {selected ? (
            <SquareCheck className="w-4 h-4 text-blue-500" />
          ) : (
            <Square className="w-4 h-4 text-gray-400" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              {c.articleItemCode}
            </span>
            <span className="text-sm text-gray-600 truncate">{c.articleItemName}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Feld:{' '}
            <span className="font-medium">{FIELD_LABELS[c.field] ?? c.field}</span>
            {' · '}
            Mandant: <span className="font-medium">{c.tenantCode}</span>
            {c.createdAt && (
              <>
                {' · '}
                <span title={new Date(c.createdAt).toLocaleString('de-DE')}>
                  seit {formatAge(c.createdAt)}
                </span>
              </>
            )}
          </p>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
              <p className="text-xs font-medium text-blue-600 mb-1">Zentraler Wert (lokal)</p>
              <p className="text-sm font-medium text-gray-900 break-words">
                {isPrice
                  ? formatCurrency(parseFloat(c.centralValue || '0'))
                  : c.centralValue || '—'}
              </p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
              <p className="text-xs font-medium text-orange-600 mb-1">
                SAP-Wert ({c.tenantCode})
              </p>
              <p className="text-sm font-medium text-gray-900 break-words">
                {isPrice
                  ? formatCurrency(parseFloat(c.remoteValue || '0'))
                  : c.remoteValue || '—'}
              </p>
            </div>
          </div>

          <div className="mt-2.5 flex items-center flex-wrap gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => onResolve('local')}
              disabled={isPending}
            >
              Lokal behalten
            </Button>
            <Button
              variant="accent"
              size="xs"
              onClick={() => onResolve('remote')}
              disabled={isPending}
            >
              SAP-Wert übernehmen
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={onDismiss}
              disabled={isPending}
            >
              Verwerfen
            </Button>
            {isPrice && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setShowPriceException(true)}
                disabled={isPending}
              >
                <ShieldAlert className="w-3.5 h-3.5" />
                Preisausnahme anlegen
              </Button>
            )}
          </div>
        </div>
      </div>

      {showPriceException && c.tenantId && (
        <PriceExceptionDialog
          articleId={c.articleId}
          tenantId={c.tenantId}
          tenantCode={c.tenantCode ?? ''}
          onClose={() => setShowPriceException(false)}
        />
      )}
    </div>
  )
}
